import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/auth/AuthProvider";
import { ProtectedRoute } from "@/app/ProtectedRoute";
import { Shell } from "@/app/Shell";
import { AdminsScreen } from "@/screens/AdminsScreen";
import { ArticleScreen } from "@/screens/ArticleScreen";
import { ArticlesScreen } from "@/screens/ArticlesScreen";
import { AssetsScreen } from "@/screens/AssetsScreen";
import { BlocksScreen } from "@/screens/BlocksScreen";
import { FloorsScreen } from "@/screens/FloorsScreen";
import { AuditScreen } from "@/screens/AuditScreen";
import { CustomerScreen } from "@/screens/CustomerScreen";
import { CustomersScreen } from "@/screens/CustomersScreen";
import { DashboardScreen } from "@/screens/DashboardScreen";
import { EsimScreen } from "@/screens/EsimScreen";
import { EsimsScreen } from "@/screens/EsimsScreen";
import { LogsScreen } from "@/screens/LogsScreen";
import { OrderScreen } from "@/screens/OrderScreen";
import { OrdersScreen } from "@/screens/OrdersScreen";
import { PaymentsScreen } from "@/screens/PaymentsScreen";
import { ProductsScreen } from "@/screens/ProductsScreen";
import { PublishScreen } from "@/screens/PublishScreen";
import { SeoScreen } from "@/screens/SeoScreen";
import { SettingsScreen } from "@/screens/SettingsScreen";
import { StatsScreen } from "@/screens/StatsScreen";
import { CouponsScreen } from "@/screens/CouponsScreen";
import { TranslationsScreen } from "@/screens/TranslationsScreen";

/**
 * The whole panel.
 *
 * `BrowserRouter` with no basename: this SPA owns `0722.esimwithnumber.com`
 * and is served from its root. Pages needs `public/_redirects` to send every
 * unknown path to index.html, which is what makes a deep link to
 * /orders/ABC1234 work on a cold load.
 *
 * THERE IS NO /login ROUTE. `ProtectedRoute` renders the sign-in screen in
 * place, so a session expiring leaves the operator on the URL they were on and
 * signing back in returns them to it -- rather than to a dashboard, having lost
 * where they were.
 */
export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProtectedRoute>
          <Routes>
            <Route element={<Shell />}>
              <Route index element={<DashboardScreen />} />
              <Route path="stats" element={<StatsScreen />} />
              <Route path="orders" element={<OrdersScreen />} />
              <Route path="orders/:code" element={<OrderScreen />} />
              <Route path="esims" element={<EsimsScreen />} />
              <Route path="esims/:iccid" element={<EsimScreen />} />
              <Route path="customers" element={<CustomersScreen />} />
              {/* The address is the id, so it is encoded into the path the
                  same way the list encodes it -- an address may contain a
                  `+`, and a `+` in a raw path segment decodes as a space. */}
              <Route path="customers/:email" element={<CustomerScreen />} />
              <Route path="payments" element={<PaymentsScreen />} />
              <Route path="seo" element={<SeoScreen />} />
              <Route path="articles" element={<ArticlesScreen />} />
              {/* The editor is a child PATH rather than a modal: writing an
                  article is where an operator spends an hour, and an hour's
                  work must survive a reload and be linkable to a colleague. */}
              <Route path="articles/:id" element={<ArticleScreen />} />
              <Route path="assets" element={<AssetsScreen />} />
              <Route path="translations" element={<TranslationsScreen />} />
              <Route path="publish" element={<PublishScreen />} />
              <Route path="products" element={<ProductsScreen />} />
              <Route path="settings" element={<SettingsScreen />} />
              <Route path="admins" element={<AdminsScreen />} />
              <Route path="blocks" element={<BlocksScreen />} />
              <Route path="coins" element={<FloorsScreen />} />
              <Route path="coupons" element={<CouponsScreen />} />
              {/* An older address for this screen: a bookmark to it lands
                  here rather than on the dashboard. */}
              <Route path="coin-minimums" element={<Navigate to="/coins" replace />} />
              <Route path="audit" element={<AuditScreen />} />
              <Route path="logs" element={<LogsScreen />} />
              {/* An unknown path goes to the dashboard rather than to a 404
                  screen: every path in here is one this app generates, so an
                  unknown one is a stale bookmark, and the dashboard is where
                  somebody with a stale bookmark wants to start. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </ProtectedRoute>
      </AuthProvider>
    </BrowserRouter>
  );
}
